import React, { useState, useEffect } from 'react';
import { Calendar, ChevronLeft, ChevronRight, TrendingUp, Filter, X } from 'lucide-react';
import { tourFileAPI } from '../services/api';

interface ItineraryRow {
  date: string;
  day?: string;
  sector?: string;
  flights?: string;
  flightNumber?: string;
  time?: string;
  city?: string;
  hotel?: string;
  hotelName?: string;
  roomCategory?: string;
  meals?: string | string[];
  guideName?: string;
}

interface TourFile {
  id: string;
  fileCode: string;
  tourName: string;
  pax: number;
  startDate: string;
  endDate: string;
  itinerary?: ItineraryRow[];
  status: string;
  foreignTourOperator?: string;
  transports?: Array<{type: string; provider: string}>;
  guides?: Array<{id: string; name: string; type: string}>;
}

interface ItineraryDetail extends ItineraryRow {
  tourFileId: string;
  fileCode: string;
  tourName: string;
  pax: number;
  foreignTourOperator?: string;
  transportType?: string;
  guideNames?: string;
  status: string;
  hotelDisplay: string;
  mealsDisplay: string;
}

export function MovementChart() {
  const [tours, setTours] = useState<TourFile[]>([]);
  const [itineraryDetails, setItineraryDetails] = useState<ItineraryDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date(2026, 2));
  const [viewMode, setViewMode] = useState<'itinerary' | 'month'>('itinerary');
  
  const [filterCity, setFilterCity] = useState('');
  const [filterTransport, setFilterTransport] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const parseJsonArray = (value: any): any[] => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  const getDayFromDate = (value?: string): string => {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';
    return dayNames[parsed.getDay()];
  };

  const getLiveStatus = (startDate: string, endDate: string): string => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = new Date(startDate);
    const end = new Date(endDate);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    if (today > end) return 'COMPLETED';
    if (today >= start && today <= end) return 'IN_PROGRESS';
    return 'UPCOMING';
  };

  useEffect(() => {
    loadTours();
  }, []);

  const loadTours = async () => {
    try {
      setLoading(true);
      const response = await tourFileAPI.getAll();
      const rawTours = Array.isArray(response?.data)
        ? response.data
        : (Array.isArray(response?.data?.tourFiles) ? response.data.tourFiles : []);

      if (response.success && rawTours.length > 0) {
        const tourData = rawTours.map((tour: any) => ({
          id: tour.id,
          fileCode: tour.fileCode,
          tourName: tour.tourName,
          pax: tour.pax || 1,
          startDate: tour.startDate,
          endDate: tour.endDate,
          itinerary: parseJsonArray(tour.itinerary),
          transports: parseJsonArray(tour.transports),
          guides: parseJsonArray(tour.guides),
          status: tour.status || getLiveStatus(tour.startDate, tour.endDate),
          foreignTourOperator: tour.foreignTourOperator,
        }));
        setTours(tourData);
        buildItineraryDetails(tourData);
      } else {
        setTours([]);
        setItineraryDetails([]);
      }
    } catch (error) {
      console.error('Failed to load tours:', error);
    } finally {
      setLoading(false);
    }
  };

  const buildItineraryDetails = (tourList: TourFile[]) => {
    const details: ItineraryDetail[] = [];
    tourList.forEach(tour => {
      const transportType = (tour.transports || [])
        .map((t: any) => t?.type)
        .filter(Boolean)
        .join(', ');
      const guideNames = (tour.guides || [])
        .map((g: any) => g?.name)
        .filter(Boolean)
        .join(', ');

      if (tour.itinerary && tour.itinerary.length > 0) {
        tour.itinerary.forEach(row => {
          const mealsValue = Array.isArray(row.meals)
            ? row.meals.join(', ')
            : (row.meals || '');
          const hotelDisplay = row.hotel || row.hotelName || '';

          details.push({
            ...row,
            day: row.day || getDayFromDate(row.date),
            flights: row.flights || row.flightNumber || row.time || '',
            hotel: hotelDisplay,
            tourFileId: tour.id,
            fileCode: tour.fileCode,
            tourName: tour.tourName,
            pax: tour.pax,
            foreignTourOperator: tour.foreignTourOperator,
            transportType,
            guideNames: row.guideName || guideNames,
            status: tour.status,
            hotelDisplay,
            mealsDisplay: mealsValue,
          });
        });
      }
    });
    setItineraryDetails(details);
  };

  const getFilteredItinerary = (): ItineraryDetail[] => {
    return itineraryDetails.filter(item => {
      const cityValue = (item.city || '').toLowerCase();
      if (filterCity && !cityValue.includes(filterCity.toLowerCase())) return false;
      if (filterTransport) {
        const hasTransport = (item.transportType || '').toLowerCase().includes(filterTransport.toLowerCase());
        if (!hasTransport) return false;
      }
      if (filterDate) {
        const selectedDate = new Date(filterDate);
        selectedDate.setHours(0, 0, 0, 0);

        const tour = tours.find((t) => t.id === item.tourFileId);
        if (!tour) return false;

        const startDate = new Date(tour.startDate);
        const endDate = new Date(tour.endDate);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(0, 0, 0, 0);

        if (selectedDate < startDate || selectedDate > endDate) return false;
      }
      return true;
    });
  };

  const getUniqueCities = (): string[] => {
    return Array.from(new Set(itineraryDetails.map(item => item.city).filter(Boolean) as string[])).sort();
  };

  const getUniqueTransports = (): string[] => {
    const transports = new Set<string>();
    tours.forEach(tour => {
      tour.transports?.forEach(t => transports.add(t.type));
    });
    return Array.from(new Set(transports)).sort();
  };

  const getFilteredTours = (): TourFile[] => {
    return tours.filter((tour) => {
      if (filterTransport) {
        const hasTransport = (tour.transports || []).some((t: any) =>
          (t?.type || '').toLowerCase().includes(filterTransport.toLowerCase())
        );
        if (!hasTransport) return false;
      }

      if (filterCity) {
        const itineraryRows = parseJsonArray(tour.itinerary);
        const hasCity = itineraryRows.some((row: any) =>
          (row?.city || '').toLowerCase().includes(filterCity.toLowerCase())
        );
        if (!hasCity) return false;
      }

      if (filterDate) {
        const selectedDate = new Date(filterDate);
        selectedDate.setHours(0, 0, 0, 0);
        const startDate = new Date(tour.startDate);
        const endDate = new Date(tour.endDate);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(0, 0, 0, 0);
        if (selectedDate < startDate || selectedDate > endDate) return false;
      }

      return true;
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED': return 'bg-green-100 text-green-800';
      case 'IN_PROGRESS': return 'bg-blue-100 text-blue-800';
      case 'UPCOMING': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const goToPreviousMonth = () => {
    const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1);
    setCurrentDate(newDate);
  };

  const goToNextMonth = () => {
    const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1);
    setCurrentDate(newDate);
  };

  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const getToursForDate = (date: Date): TourFile[] => {
    return tours.filter(tour => {
      const startDate = new Date(tour.startDate);
      const endDate = new Date(tour.endDate);
      const checkDate = new Date(date);
      return startDate <= checkDate && checkDate <= endDate;
    });
  };

  const filteredData = getFilteredItinerary();
  const filteredTours = getFilteredTours();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <TrendingUp className="w-8 h-8 text-purple-600" />
              Tour Movement Chart
            </h1>
            <p className="text-gray-600 mt-1">Track tours by date, city, transport, and guide assignments</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4 bg-white p-4 rounded-lg shadow">
          <div className="flex items-center gap-2">
            <button onClick={goToPreviousMonth} className="p-2 hover:bg-gray-100 rounded-md transition">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="text-center min-w-32 text-sm text-gray-600">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </div>
            <button onClick={goToNextMonth} className="p-2 hover:bg-gray-100 rounded-md transition">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setViewMode('itinerary')}
              className={`px-4 py-2 rounded-md font-medium transition ${
                viewMode === 'itinerary'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              📋 Itinerary
            </button>
            <button
              onClick={() => setViewMode('month')}
              className={`px-4 py-2 rounded-md font-medium transition ${
                viewMode === 'month'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Calendar className="w-4 h-4 inline mr-2" />
              Calendar
            </button>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="px-4 py-2 rounded-md font-medium bg-blue-100 text-blue-700 hover:bg-blue-200 transition flex items-center gap-2"
            >
              <Filter className="w-4 h-4" />
              Filter
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="mt-4 bg-white p-4 rounded-lg shadow border border-blue-200">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-semibold text-gray-900">Filter Options</h3>
              <button onClick={() => setShowFilters(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">City</label>
                <select
                  value={filterCity}
                  onChange={(e) => setFilterCity(e.target.value)}
                  className="w-full px-2 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Cities</option>
                  {getUniqueCities().map(city => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Transport</label>
                <select
                  value={filterTransport}
                  onChange={(e) => setFilterTransport(e.target.value)}
                  className="w-full px-2 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Vehicles</option>
                  {getUniqueTransports().map(transport => (
                    <option key={transport} value={transport}>{transport}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  value={filterDate}
                  onChange={(e) => setFilterDate(e.target.value)}
                  className="w-full px-2 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              {(filterCity || filterTransport || filterDate) && (
                <div className="flex items-end">
                  <button
                    onClick={() => {
                      setFilterCity('');
                      setFilterTransport('');
                      setFilterDate('');
                    }}
                    className="w-full px-3 py-2 bg-red-100 text-red-700 hover:bg-red-200 rounded-md text-sm font-medium transition"
                  >
                    Clear All
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {viewMode === 'itinerary' && (
        <div className="space-y-4">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="px-4 py-3 border-b bg-gray-50">
              <h3 className="font-semibold text-gray-900">Tour Summary (From Tour Files)</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 text-gray-800">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">FTO</th>
                    <th className="px-3 py-2 text-left font-semibold">Tour Name</th>
                    <th className="px-3 py-2 text-left font-semibold">Tour Code</th>
                    <th className="px-3 py-2 text-center font-semibold">Total Pax</th>
                    <th className="px-3 py-2 text-left font-semibold">Vehicle</th>
                    <th className="px-3 py-2 text-left font-semibold">Guide</th>
                    <th className="px-3 py-2 text-left font-semibold">Date Range</th>
                    <th className="px-3 py-2 text-left font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredTours.map((tour) => {
                    const vehicle = (tour.transports || []).map((t: any) => t?.type).filter(Boolean).join(', ') || '-';
                    const guides = (tour.guides || []).map((g: any) => g?.name).filter(Boolean).join(', ') || '-';
                    return (
                      <tr key={tour.id} className="hover:bg-gray-50 transition">
                        <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{tour.foreignTourOperator || '-'}</td>
                        <td className="px-3 py-2 font-medium text-gray-900 whitespace-nowrap">{tour.tourName}</td>
                        <td className="px-3 py-2 text-purple-600 font-medium whitespace-nowrap">{tour.fileCode}</td>
                        <td className="px-3 py-2 text-center font-semibold text-gray-800">{tour.pax}</td>
                        <td className="px-3 py-2 text-gray-700">{vehicle}</td>
                        <td className="px-3 py-2 text-gray-700">{guides}</td>
                        <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{new Date(tour.startDate).toLocaleDateString()} - {new Date(tour.endDate).toLocaleDateString()}</td>
                        <td className="px-3 py-2"><span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${getStatusColor(tour.status)}`}>{tour.status}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="px-4 py-3 border-b bg-gray-50">
              <h3 className="font-semibold text-gray-900">Date-wise Itinerary Movement</h3>
            </div>
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gradient-to-r from-purple-600 to-blue-600 text-white sticky top-0">
                <tr>
                  <th className="px-3 py-3 text-left font-semibold">FTO</th>
                  <th className="px-3 py-3 text-left font-semibold">File Code</th>
                  <th className="px-3 py-3 text-left font-semibold">Tour Name</th>
                  <th className="px-3 py-3 text-center font-semibold">Pax</th>
                  <th className="px-3 py-3 text-left font-semibold">Date</th>
                  <th className="px-3 py-3 text-left font-semibold">Day</th>
                  <th className="px-3 py-3 text-left font-semibold">Sector</th>
                  <th className="px-3 py-3 text-left font-semibold">Flights/Trains</th>
                  <th className="px-3 py-3 text-left font-semibold">Vehicle</th>
                  <th className="px-3 py-3 text-left font-semibold">Guide</th>
                  <th className="px-3 py-3 text-left font-semibold">City</th>
                  <th className="px-3 py-3 text-left font-semibold">Hotel</th>
                  <th className="px-3 py-3 text-left font-semibold">Room</th>
                  <th className="px-3 py-3 text-left font-semibold">Meals</th>
                  <th className="px-3 py-3 text-left font-semibold">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredData.length === 0 ? (
                  <tr>
                    <td colSpan={15} className="px-4 py-8 text-center text-gray-500">
                      No itinerary rows found in saved Tour Files. Tour Summary is shown above.
                    </td>
                  </tr>
                ) : (
                  filteredData.map((item, idx) => (
                    <tr key={idx} className="hover:bg-gray-50 transition">
                      <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{item.foreignTourOperator || '-'}</td>
                      <td className="px-3 py-2 font-medium text-purple-600 whitespace-nowrap">{item.fileCode}</td>
                      <td className="px-3 py-2 font-medium text-gray-900 max-w-xs truncate">{item.tourName}</td>
                      <td className="px-3 py-2 text-center"><span className="inline-block bg-green-100 text-green-800 px-2 py-1 rounded font-bold">{item.pax}</span></td>
                      <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{item.date}</td>
                      <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{item.day || '-'}</td>
                      <td className="px-3 py-2 text-gray-700">{item.sector || '-'}</td>
                      <td className="px-3 py-2 text-gray-700 text-xs">{item.flights || '-'}</td>
                      <td className="px-3 py-2 text-gray-700 text-xs">{item.transportType || '-'}</td>
                      <td className="px-3 py-2 text-gray-700 text-xs">{item.guideNames || '-'}</td>
                      <td className="px-3 py-2"><span className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">{item.city || '-'}</span></td>
                      <td className="px-3 py-2 text-gray-700 max-w-xs truncate">{item.hotelDisplay || '-'}</td>
                      <td className="px-3 py-2 text-gray-700 text-xs">{item.roomCategory || '-'}</td>
                      <td className="px-3 py-2 text-gray-700 text-xs">{item.mealsDisplay || '-'}</td>
                      <td className="px-3 py-2"><span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${getStatusColor(item.status)}`}>{item.status}</span></td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 grid grid-cols-4 gap-4">
            <div className="text-center"><div className="text-2xl font-bold text-purple-600">{filteredData.length}</div><div className="text-xs text-gray-600">Itinerary Rows</div></div>
            <div className="text-center"><div className="text-2xl font-bold text-blue-600">{new Set(filteredTours.map(t => t.id)).size}</div><div className="text-xs text-gray-600">Tours</div></div>
            <div className="text-center"><div className="text-2xl font-bold text-green-600">{new Set(filteredData.map(d => d.city).filter(Boolean)).size}</div><div className="text-xs text-gray-600">Cities</div></div>
            <div className="text-center"><div className="text-2xl font-bold text-orange-600">{filteredTours.reduce((sum, t) => sum + t.pax, 0)}</div><div className="text-xs text-gray-600">Total Pax</div></div>
          </div>
        </div>
        </div>
      )}

      {viewMode === 'month' && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="grid grid-cols-7 gap-2 mb-2">
            {dayNames.map(day => (
              <div key={day} className="text-center font-bold text-gray-700 py-2">{day}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-2">
            {Array.from({ length: getFirstDayOfMonth(currentDate) }).map((_, idx) => (
              <div key={`empty-${idx}`} className="aspect-square" />
            ))}
            {Array.from({ length: getDaysInMonth(currentDate) }).map((_, idx) => {
              const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), idx + 1);
              const dayTours = getToursForDate(date);
              const isToday = date.getDate() === new Date().getDate() && date.getMonth() === new Date().getMonth() && date.getFullYear() === new Date().getFullYear();
              return (
                <div key={idx + 1} className={`aspect-square p-2 rounded-lg border-2 overflow-y-auto transition ${isToday ? 'border-purple-600 bg-purple-50' : 'border-gray-200 bg-gray-50 hover:border-purple-300'}`} style={{ minHeight: '120px' }}>
                  <div className={`font-bold mb-1 ${isToday ? 'text-purple-600' : 'text-gray-700'}`}>{idx + 1}</div>
                  <div className="space-y-1">
                    {dayTours.slice(0, 2).map(tour => (
                      <div key={tour.id} className="text-xs bg-gradient-to-r from-purple-500 to-blue-500 text-white p-1 rounded truncate hover:shadow-md transition cursor-pointer" title={`${tour.tourName} - ${tour.pax} pax`}>
                        {tour.tourName.substring(0, 12)}
                      </div>
                    ))}
                    {dayTours.length > 2 && <div className="text-xs text-purple-600 font-medium">+{dayTours.length - 2} more</div>}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-6 grid grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white p-4 rounded-lg">
              <div className="text-2xl font-bold">{tours.length}</div>
              <div className="text-sm">Total Tours</div>
            </div>
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-4 rounded-lg">
              <div className="text-2xl font-bold">{tours.reduce((sum, t) => sum + t.pax, 0)}</div>
              <div className="text-sm">Total Pax</div>
            </div>
            <div className="bg-gradient-to-br from-green-500 to-green-600 text-white p-4 rounded-lg">
              <div className="text-2xl font-bold">{new Set(tours.flatMap(t => t.transports?.map(tr => tr.type) || [])).size}</div>
              <div className="text-sm">Vehicles</div>
            </div>
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white p-4 rounded-lg">
              <div className="text-2xl font-bold">{new Set(tours.map(t => t.foreignTourOperator).filter(Boolean)).size}</div>
              <div className="text-sm">Operators</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
